import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/select';
import { Checkbox } from '@/components/ui/checkbox';
import type { NodeStruct } from '@/types/node-types';
import {
  getSwitchConfig,
  validateSwitchConfig,
} from '@/pages/workflow/helper/functional-control-flow';
import { useNodeStore } from '@/stores/node-store';
import { edgesForBranch } from '@/pages/workflow/helper/switch-branch-sync';

const FAIL = 'Fail the run';
const DEFAULT_BRANCH = 'Go to a default branch';
const DEFAULT_LABEL_SUGGESTION = 'otherwise';

interface SwitchFallbackControlProps {
  nodeId: string;
  struct: NodeStruct;
  onChange: (fieldId: 'null_behavior' | 'default_label', value: string) => void;
  disabled?: boolean;
}

/**
 * One control for what the backend splits into `default_label` and
 * `null_behavior`: "If nothing matches: fail the run / go to a default
 * branch", with the null case folded in as a checkbox.
 */
export function SwitchFallbackControl({
  nodeId,
  struct,
  onChange,
  disabled = false,
}: SwitchFallbackControlProps) {
  const config = getSwitchConfig(struct);
  const hasDefault = config.defaultLabel !== null;
  const nullToDefault = config.nullBehavior === 'default';
  const { edges, renameSwitchBranch } = useNodeStore(
    useShallow((state) => ({
      edges: state.edges,
      renameSwitchBranch: state.renameSwitchBranch,
    })),
  );

  const [draftLabel, setDraftLabel] = useState(config.defaultLabel ?? '');
  useEffect(() => {
    setDraftLabel(config.defaultLabel ?? '');
  }, [config.defaultLabel]);

  const labelErrors = validateSwitchConfig({
    ...config,
    defaultLabel: hasDefault ? draftLabel || null : null,
  }).filter((error) => error.path === 'default_label');

  const commitLabel = () => {
    const next = draftLabel.trim();
    if (!next || next === config.defaultLabel || labelErrors.length > 0) {
      return;
    }
    if (config.defaultLabel) {
      renameSwitchBranch(nodeId, config.defaultLabel, next);
    }
    onChange('default_label', next);
  };

  const connectedCount = config.defaultLabel
    ? edgesForBranch(edges, nodeId, config.defaultLabel).length
    : 0;

  return (
    <div className='w-full mb-4 px-3' data-testid='switch-fallback-control'>
      <div className='flex items-center gap-1 mb-1 min-h-[36px]'>
        <Label className='text-sm'>If nothing matches</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className='h-4 w-4 text-muted-foreground cursor-help' />
          </TooltipTrigger>
          <TooltipContent>
            <p className='max-w-xs'>
              Cases are tried top to bottom; the first match wins. Choose what
              happens when none of them match.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
      <Select
        options={[FAIL, DEFAULT_BRANCH]}
        selected={hasDefault ? DEFAULT_BRANCH : FAIL}
        allowAllOption={false}
        disabled={disabled}
        className='w-full h-auto'
        onSelect={(choice) => {
          if (choice === FAIL) {
            if (connectedCount > 0) {
              const confirmed = window.confirm(
                `The default branch "${config.defaultLabel}" has ${connectedCount} connection${connectedCount === 1 ? '' : 's'}. Remove the default branch anyway? Its connections will be removed when you save.`,
              );
              if (!confirmed) return;
            }
            onChange('default_label', '');
            onChange('null_behavior', 'error');
          } else if (choice === DEFAULT_BRANCH && !hasDefault) {
            onChange('default_label', DEFAULT_LABEL_SUGGESTION);
            onChange('null_behavior', 'default');
          }
        }}
      />
      {hasDefault && (
        <div className='mt-2 space-y-2'>
          <div>
            <Label className='text-xs text-muted-foreground'>
              Default branch name
            </Label>
            <Input
              value={draftLabel}
              disabled={disabled}
              placeholder={DEFAULT_LABEL_SUGGESTION}
              aria-label='Default branch name'
              data-testid='switch-default-label'
              onChange={(event) => setDraftLabel(event.target.value)}
              onBlur={commitLabel}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitLabel();
                }
              }}
              className={labelErrors.length ? 'border-red-500' : ''}
            />
            {labelErrors.length > 0 && (
              <p className='text-xs text-red-500 mt-1'>
                {labelErrors[0].message}
              </p>
            )}
            {connectedCount > 0 && labelErrors.length === 0 && (
              <p className='text-xs text-muted-foreground mt-1'>
                Renaming keeps its {connectedCount} connection
                {connectedCount === 1 ? '' : 's'}.
              </p>
            )}
          </div>
          <label className='flex items-start gap-2 text-xs text-muted-foreground cursor-pointer'>
            <Checkbox
              checked={nullToDefault}
              disabled={disabled}
              aria-label='Also send a null value to the default branch'
              onCheckedChange={(checked) =>
                onChange('null_behavior', checked ? 'default' : 'error')
              }
              className='mt-0.5'
            />
            <span>
              Also send a null value to this branch
              {!nullToDefault && ' (otherwise a null value fails the run)'}
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
